import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SharedNav from '../../components/feature/SharedNav';
import Footer from '../home/components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  '@id': `${SITE_URL}/private-health-insurance-kerrville-tx#breadcrumb`,
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Private Health Insurance Kerrville TX',
      item: `${SITE_URL}/private-health-insurance-kerrville-tx`,
    },
  ],
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE_URL}/private-health-insurance-kerrville-tx#webpage`,
  url: `${SITE_URL}/private-health-insurance-kerrville-tx`,
  name: 'Private Health Insurance Kerrville TX | Individual & Family Plans | West Insurance',
  description: 'Private health insurance plans for individuals and families in Kerrville, TX. Compare PPO, HMO, ACA Marketplace & short-term plans. Free quotes. Call (830) 203-6201. West Health Insurance.',
  isPartOf: { '@id': `${SITE_URL}/#website` },
  breadcrumb: { '@id': `${SITE_URL}/private-health-insurance-kerrville-tx#breadcrumb` },
};

const schemaData = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Private Health Insurance Kerrville TX',
  description: 'Individual and family private health insurance plans in Kerrville, TX. Affordable PPO, HMO, short-term, and ACA marketplace plans for Kerr County residents.',
  provider: {
    '@type': 'InsuranceAgency',
    name: 'West Health Insurance',
    telephone: '+18302036201',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '243 La Reata Rd S',
      addressLocality: 'Kerrville',
      addressRegion: 'TX',
      postalCode: '78028',
      addressCountry: 'US',
    },
  },
  areaServed: { '@type': 'City', name: 'Kerrville', containedInPlace: { '@type': 'State', name: 'Texas' } },
  url: `${SITE_URL}/private-health-insurance-kerrville-tx`,
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is private health insurance in Kerrville, TX?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Private health insurance in Kerrville refers to coverage purchased outside of employer benefits — including individual plans, family plans, ACA Marketplace plans, short-term plans, and catastrophic coverage. West Health Insurance helps Kerrville residents compare and enroll in the right plan.',
      },
    },
    {
      '@type': 'Question',
      name: 'How much does private health insurance cost in Kerrville, Texas?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Private health insurance costs in Kerrville, TX vary based on age, plan type, and coverage level. ACA Marketplace plans may qualify for subsidies that significantly reduce monthly premiums. Call West Health Insurance at (830) 203-6201 for a free personalized quote.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I get a private health insurance plan outside open enrollment in Kerrville?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Qualifying life events such as losing employer coverage, getting married, or having a child trigger a Special Enrollment Period. Short-term health plans are also available year-round for temporary coverage. West Health Insurance can help you determine your eligibility.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the difference between PPO and HMO health plans available in Kerrville?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'PPO plans (Preferred Provider Organization) offer more flexibility to see any doctor without a referral, while HMO plans (Health Maintenance Organization) typically require you to use a network of doctors and need referrals to see specialists. Our agents at West Health Insurance will help you choose the best fit for your needs and budget.',
      },
    },
    {
      '@type': 'Question',
      name: 'Does West Health Insurance offer ACA Marketplace plans in Kerrville?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. West Health Insurance is licensed to help Kerrville and Kerr County residents shop, compare, and enroll in ACA Marketplace plans. Many clients qualify for premium tax credits that dramatically lower their monthly costs. Call (830) 203-6201 for a free consultation.',
      },
    },
  ],
};

export default function PrivateHealthInsurancePage() {
  useEffect(() => {
    document.title = 'Private Health Insurance Kerrville TX | Individual & Family Plans | West Insurance';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Private health insurance plans for individuals and families in Kerrville, TX. Compare PPO, HMO, ACA Marketplace & short-term plans. Free quotes. Call (830) 203-6201. West Health Insurance.');
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${SITE_URL}/private-health-insurance-kerrville-tx`);

    const s1 = document.createElement('script');
    s1.type = 'application/ld+json';
    s1.id = 'schema-private-service';
    s1.textContent = JSON.stringify(schemaData);
    document.head.appendChild(s1);

    const s2 = document.createElement('script');
    s2.type = 'application/ld+json';
    s2.id = 'schema-private-faq';
    s2.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(s2);

    const s3 = document.createElement('script');
    s3.type = 'application/ld+json';
    s3.id = 'schema-private-breadcrumb';
    s3.textContent = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(s3);

    const s4 = document.createElement('script');
    s4.type = 'application/ld+json';
    s4.id = 'schema-private-webpage';
    s4.textContent = JSON.stringify(webPageSchema);
    document.head.appendChild(s4);

    return () => {
      document.title = 'West Health Insurance Kerrville TX | Private Health, Group & Medicare';
      s1.parentNode?.removeChild(s1);
      s2.parentNode?.removeChild(s2);
      s3.parentNode?.removeChild(s3);
      s4.parentNode?.removeChild(s4);
    };
  }, []);

  const plans = [
    {
      icon: 'ri-user-heart-line',
      name: 'Individual Health Plans',
      desc: 'Tailored coverage for single adults who need flexible, affordable private health insurance in Kerrville. Choose your deductible, network, and premium.',
      highlight: 'Most Popular',
      features: ['PPO & HMO options', 'Preventive care included', 'Telehealth access', 'Rx drug coverage'],
    },
    {
      icon: 'ri-home-heart-line',
      name: 'Family Health Plans',
      desc: 'Comprehensive family health coverage for Kerrville households. One plan to protect your spouse, children, and yourself under a single deductible.',
      highlight: '',
      features: ['Pediatric dental & vision', 'Maternity coverage', 'Wellness visits', 'Emergency care'],
    },
    {
      icon: 'ri-time-line',
      name: 'Short-Term Plans',
      desc: 'Bridge health coverage during gaps in insurance — between jobs, waiting for employer coverage, or transitioning plans. Fast approval for Kerrville residents.',
      highlight: 'Fast Approval',
      features: ['Approved in 24hrs', 'Flexible duration', 'Low premiums', 'Emergency coverage'],
    },
    {
      icon: 'ri-store-2-line',
      name: 'ACA Marketplace Plans',
      desc: 'Affordable Care Act plans available during Open Enrollment or Special Enrollment Periods. Many Kerrville residents qualify for subsidies that reduce costs.',
      highlight: 'Subsidies Available',
      features: ['Tax credit eligible', 'Essential benefits', 'Pre-existing conditions covered', 'Annual enrollment'],
    },
  ];

  const faqs = faqSchema.mainEntity;

  return (
    <div className="min-h-screen bg-white">
      <SharedNav />

      {/* Hero */}
      <section className="relative pt-20 min-h-[600px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/e7cc453c26cd3257494c457132432f5d.jpg"
            alt="Private Health Insurance for Families and Individuals Kerrville TX"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/20"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 w-full">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#0077B6]/80 backdrop-blur-sm rounded-full mb-6">
              <i className="ri-map-pin-line text-white text-sm"></i>
              <span className="text-white text-sm font-medium">Kerrville &amp; Kerr County, Texas</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              Private Health Insurance<br />
              <span className="text-4xl md:text-5xl">in Kerrville, TX</span>
            </h1>
            <p className="text-lg text-white/90 mb-8 leading-relaxed">
              Individual and family health insurance plans tailored for Kerrville and Kerr County residents. Compare PPO, HMO, ACA Marketplace, and short-term plans — and get expert guidance at no extra cost.
            </p>
            <div className="flex items-center gap-4 flex-wrap">
              <Link to="/#contact" className="px-8 py-4 bg-[#0077B6] text-white font-semibold rounded-lg hover:bg-[#005F8C] transition-all whitespace-nowrap cursor-pointer">
                Get a Free Quote
              </Link>
              <a href="tel:830-203-6201" className="px-8 py-4 bg-white text-[#0077B6] font-semibold rounded-lg hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer">
                Call 830-203-6201
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Why Kerrville Residents Choose Private Health Insurance
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Not everyone gets health insurance through an employer — and that's where <strong>West Health Insurance</strong> comes in. We help self-employed individuals, freelancers, early retirees, and families in Kerrville, TX find <strong>private health insurance</strong> plans that fit their budget and lifestyle.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                With access to all major carriers in Texas, we shop the market for you and present only the plans that match your needs. There's no cost for our service — our agents are paid by the insurers, not by you.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: 'ri-shield-check-line', label: 'Licensed TX Agents' },
                  { icon: 'ri-price-tag-3-line', label: 'Free Quotes' },
                  { icon: 'ri-bank-card-line', label: 'Subsidy Guidance' },
                  { icon: 'ri-phone-line', label: 'Open 24/7' },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-3 p-4 bg-[#F0F8FF] rounded-xl">
                    <div className="w-9 h-9 flex items-center justify-center bg-[#0077B6] rounded-lg">
                      <i className={`${item.icon} text-white text-base`}></i>
                    </div>
                    <span className="text-sm font-semibold text-gray-800">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20insurance%20agent%20sitting%20across%20a%20table%20from%20a%20young%20couple%20reviewing%20health%20insurance%20documents%20in%20a%20bright%20modern%20office%20with%20plants%20and%20natural%20light%20blue%20and%20white%20tones%20warm%20professional%20atmosphere&width=700&height=550&seq=priv-agent-001&orientation=landscape"
                alt="West Health Insurance Agent Helping Kerrville Clients"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="py-20 bg-[#F0F8FF]">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Private Health Insurance Plans We Offer
          </h2>
          <p className="text-gray-600 text-center max-w-2xl mx-auto mb-14 leading-relaxed">
            Every Kerrville resident's situation is different. We work with all major Texas carriers to find the right private health insurance plan for you.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan) => (
              <div key={plan.name} className="bg-white rounded-2xl p-6 relative flex flex-col">
                {plan.highlight && (
                  <div className="absolute -top-3 left-6">
                    <span className="px-3 py-1 bg-[#0077B6] text-white text-xs font-bold rounded-full">{plan.highlight}</span>
                  </div>
                )}
                <div className="w-12 h-12 flex items-center justify-center bg-[#F0F8FF] rounded-xl mb-4">
                  <i className={`${plan.icon} text-[#0077B6] text-2xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{plan.name}</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-grow">{plan.desc}</p>
                <ul className="space-y-2 mb-6">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-gray-700">
                      <i className="ri-check-line text-[#0077B6]"></i>
                      {f}
                    </li>
                  ))}
                </ul>
                <a href="tel:830-203-6201" className="block text-center px-4 py-2 bg-[#0077B6] text-white text-sm font-semibold rounded-lg hover:bg-[#005F8C] transition-colors whitespace-nowrap cursor-pointer">
                  Get Quote
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 text-center mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Frequently Asked Questions
          </h2>
          <p className="text-gray-600 text-center mb-14">Common questions about private health insurance in Kerrville, TX</p>
          <div className="space-y-4">
            {faqs.map((faq) => (
              <details key={faq.name} className="group bg-[#F0F8FF] rounded-xl p-6 cursor-pointer">
                <summary className="flex items-center justify-between font-semibold text-gray-900 list-none cursor-pointer">
                  <span>{faq.name}</span>
                  <i className="ri-add-line text-[#0077B6] text-xl group-open:hidden"></i>
                  <i className="ri-subtract-line text-[#0077B6] text-xl hidden group-open:block"></i>
                </summary>
                <p className="mt-4 text-gray-600 text-sm leading-relaxed">{faq.acceptedAnswer.text}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0077B6]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Ready to Find Your Private Health Plan?
          </h2>
          <p className="text-white/85 text-lg mb-8 max-w-xl mx-auto">
            Our licensed agents in Kerrville, TX are ready to compare plans and find you the best private health insurance coverage at the lowest cost.
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Link to="/#contact" className="px-8 py-4 bg-white text-[#0077B6] font-semibold rounded-lg hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer">
              Get a Free Quote Today
            </Link>
            <a href="tel:830-203-6201" className="px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer">
              Call (830) 203-6201
            </a>
          </div>
          <div className="mt-10 flex items-center justify-center gap-8 flex-wrap">
            <Link to="/group-health-insurance-kerrville-tx" className="text-white/80 text-sm hover:text-white transition-colors cursor-pointer">
              &rarr; Group Health Insurance
            </Link>
            <Link to="/medicare-kerrville-tx" className="text-white/80 text-sm hover:text-white transition-colors cursor-pointer">
              &rarr; Medicare Plans
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
